// DataDirector MainPage
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Speed as DashboardIcon,
  Assignment as JobsIcon,
  Storage as DataSourcesIcon,
  Monitor as MonitoringIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';

import DashboardLayout from 'layouts/DashboardLayout';
import DashboardNavbar from 'components/RDNavbars/DashboardNavbar';
import RDFooter from 'components/RDFooter';
import DataDirectorHeader from 'components/data-director/DataDirectorHeader';
import DataDirectorTabs from '../components/data-director/DataDirectorTabs';

import { JobDefinitionsTab } from 'components/data-director/job-definitions/JobDefinitionsTab';
import MonitoringTab from 'components/data-director/monitoring/MonitoringTab';

const DataDirectorPage = () => {
  const navigate = useNavigate();
  
  // State for main tab and job subtab
  const [activeMainTab, setActiveMainTab] = useState(0); // Default to first tab
  const [activeJobTab, setActiveJobTab] = useState(0); // Default to first job tab

  // Handle main tab change
  const handleMainTabChange = (tabIndex) => {
    setActiveMainTab(tabIndex);
  };

  // Handle job tab change
  const handleJobTabChange = (event, newValue) => {
    setActiveJobTab(newValue);
  };

  const sections = [
    {
      title: 'Dashboard',
      icon: <DashboardIcon fontSize="large" color="primary" />,
      description: 'View DataDirector metrics and status at a glance',
      primaryAction: { text: 'View Dashboard', route: '/data-director/dashboard' },
      implemented: false // Indicates if this section is implemented yet
    },
    {
      title: 'Jobs',
      icon: <JobsIcon fontSize="large" color="primary" />,
      description: 'Manage job configurations and executions',
      primaryAction: { text: 'View Jobs', route: '/data-director/jobs' },
      secondaryAction: { text: 'Create New Job', route: '/data-director/jobs/new' },
      implemented: true
    },
    {
      title: 'Data Sources',
      icon: <DataSourcesIcon fontSize="large" color="primary" />,
      description: 'Configure data source adapters and credentials',
      primaryAction: { text: 'Manage Data Sources', route: '/data-director/data-sources' },
      implemented: false
    },
    {
      title: 'Monitoring',
      icon: <MonitoringIcon fontSize="large" color="primary" />,
      description: 'View detailed logs, metrics, and execution history',
      primaryAction: { text: 'View Monitoring', route: '/data-director/monitoring' },
      implemented: true
    },
    {
      title: 'Settings',
      icon: <SettingsIcon fontSize="large" color="primary" />,
      description: 'Configure system settings and user preferences',
      primaryAction: { text: 'Manage Settings', route: '/data-director/settings' },
      implemented: false
    }
  ];

  // Define the content for each main tab
  const renderMainTabContent = () => {
    switch (activeMainTab) {
      case 0: // Dashboard tab (or card view if not implemented)
        if (sections[0].implemented) {
          return (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6">Dashboard</Typography>
              <Typography variant="body1">Analytics dashboard will go here</Typography>
            </Box>
          );
        } else {
          // Show card grid for main view if Dashboard not implemented
          return renderCardGrid();
        }
      
      case 1: // Jobs tab
        return renderJobsContent();
      
      case 2: // Data Sources tab
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Data Sources functionality will be available in future releases.
            </Typography>
          </Box>
        );
      
      case 3: // Monitoring tab
        return (
          <Box sx={{ mt: 3 }}>
            <MonitoringTab />
          </Box>
        );
      
      case 4: // Settings tab
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Settings functionality will be available in future releases.
            </Typography>
          </Box>
        );
      
      default:
        return renderCardGrid();
    }
  };

  // Render the card grid for main view
  const renderCardGrid = () => {
    return (
      <Grid container spacing={3}>
        {sections.map((section, index) => (
          <Grid item xs={12} md={6} lg={4} key={index}>
            <Card 
              elevation={3}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                opacity: section.implemented ? 1 : 0.7
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  {section.icon}
                  <Typography variant="h6" component="div" ml={1}>
                    {section.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {section.description}
                </Typography>
                {!section.implemented && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block', 
                      mt: 2, 
                      color: 'warning.main',
                      fontStyle: 'italic'
                    }}
                  >
                    Coming soon in future releases
                  </Typography>
                )}
              </CardContent>
              <Divider />
              <CardActions>
                <Button 
                  size="small" 
                  onClick={() => {
                    if (section.implemented) {
                      // If implemented, switch to the appropriate tab
                      setActiveMainTab(index);
                    } else {
                      // If not implemented, just navigate to the route (which will show the cards)
                      navigate(section.primaryAction.route);
                    }
                  }}
                  disabled={!section.implemented}
                >
                  {section.primaryAction.text}
                </Button>
                {section.secondaryAction && (
                  <Button 
                    size="small" 
                    color="secondary"
                    onClick={() => {
                      if (section.implemented) {
                        setActiveMainTab(index);
                        // Additional action for "Create New Job" if needed
                        if (index === 1) { // Jobs section
                          // Here you might trigger the Create Job modal directly
                          // For now, we just activate the Jobs tab
                          setActiveMainTab(1);
                        }
                      }
                    }}
                    disabled={!section.implemented}
                  >
                    {section.secondaryAction.text}
                  </Button>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Define the content specifically for the Jobs tab
  const renderJobsContent = () => {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={activeJobTab} 
            onChange={handleJobTabChange} 
            aria-label="job management tabs"
          >
            <Tab label="Job Definitions" />
            <Tab label="Job Executions" disabled />
            <Tab label="Schedules" disabled />
          </Tabs>
        </Box>
        
        {/* Job sub-tabs content */}
        {activeJobTab === 0 && (
          <JobDefinitionsTab />
        )}
        
        {activeJobTab === 1 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Job Executions functionality will be available in future releases.
            </Typography>
          </Box>
        )}
        
        {activeJobTab === 2 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Schedules functionality will be available in future releases.
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ p: 3 }}>
        <DataDirectorHeader />
        
        {/* Use the updated DataDirectorTabs component */}
        <DataDirectorTabs 
          sections={sections} 
          value={activeMainTab}
          onChange={handleMainTabChange}
        />
        
        {/* Tab content area */}
        {renderMainTabContent()}

        {/* Phase Indicator */}
        <Paper elevation={1} sx={{ p: 2, mt: 4 }}>
          <Typography variant="subtitle2" color="primary">
            Phase 0 Implementation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Job Definition functionality is available now. Additional features coming in future releases.
          </Typography>
        </Paper>
      </Box>
      <RDFooter />
    </DashboardLayout>
  );
};

export default DataDirectorPage;